import React from 'react';

import { useFormContext } from 'react-hook-form';
import { InputAdornment } from '@mui/material';
import { LbInput, LbSelect, TextArea } from '@infoods/lifebase-ui';

import classes from './BasicInfoSection.module.scss';
import {
  LbNumericFormatInput,
  ModeAwareInput,
} from '../../../../../../shared/components/form-components';
import { FormFieldsGrid } from '../../../FormFieldsGrid/FormFieldsGrid';
import { ChallengeFormValues } from '../../../../../interfaces/ChallengeFormValues';
import { Mode } from '../../../../../../general-types';
import { useFormDependentOptions } from '../../../../../../shared/hooks/useFormDependentOptions';
import { SolutionTypeFlags } from '../../../../../../shared/constants/user-info/solutionType';
import { useCustomerTypeOptions } from './hooks/useCustomerTypeOptions';
import { getPriceView } from '../../../../../../shared/utils/stringUtils';
import { isEmptyValue } from '../../../../../../shared/utils/jsUtils';
import { getScoringRuleOptionsForCustomerType } from '../../../../../utils/getScoringRuleOptionsForCustomerType/getScoringRuleOptionsForCustomerType';
import { validateWhitespace } from '../../../../../../shared/utils/reactHookFormUtils';

type BasicInfoSectionProps = {
  mode: Mode;
  availableSolutionsFlags: SolutionTypeFlags;
  disabledEditMainFields?: boolean;
};

export const BasicInfoSection = ({
  mode,
  availableSolutionsFlags,
  disabledEditMainFields,
}: BasicInfoSectionProps) => {
  const { control, resetField } = useFormContext<ChallengeFormValues>();

  const availableCustomerTypeOptions = useCustomerTypeOptions(availableSolutionsFlags);

  const { availableOptions: availableScoringRuleOptions } = useFormDependentOptions({
    control,
    parentName: 'about.customerType',
    childName: 'about.scoringRules',
    getOptions: getScoringRuleOptionsForCustomerType,
    onResetField: (name) => {
      resetField(name, { defaultValue: '' });
    },
  });

  const generalInputProps = {
    size: 'small',
    variant: 'standard',
  } as const;

  return (
    <FormFieldsGrid mode={mode}>
      <div className={classes.spanAll}>
        <ModeAwareInput
          mode={mode}
          control={control}
          name="about.challengeName"
          label="Challenge Name"
          renderEdit={(props) => (
            <LbInput
              {...generalInputProps}
              {...props}
              rules={{
                required: 'Challenge Name is required',
                validate: { validateWhitespace },
                maxLength: {
                  value: 40,
                  message: 'Challenge Name must be 40 characters or less',
                },
              }}
            />
          )}
        />
      </div>
      <ModeAwareInput
        mode={mode}
        control={control}
        name="about.customerType"
        label="Customer Type"
        options={availableCustomerTypeOptions}
        readonly={disabledEditMainFields}
        renderEdit={(props) => (
          <LbSelect
            {...generalInputProps}
            {...props}
            rules={{ required: 'Customer Type is required' }}
          />
        )}
      />
      <ModeAwareInput
        mode={mode}
        control={control}
        name="about.scoringRules"
        label="Scoring Rules"
        options={availableScoringRuleOptions}
        readonly={disabledEditMainFields}
        renderEdit={(props) => (
          <LbSelect
            {...generalInputProps}
            {...props}
            rules={{ required: 'Scoring Rules is required' }}
            disabled={!availableScoringRuleOptions.length}
          />
        )}
      />
      <ModeAwareInput
        mode={mode}
        control={control}
        name="about.price"
        label="Price"
        readonly={disabledEditMainFields}
        renderValue={(value) =>
          isEmptyValue(value) ? 'Not set' : `${getPriceView(value)} / per customer`
        }
        renderEdit={(props) => (
          <LbNumericFormatInput
            {...generalInputProps}
            {...props}
            prefix="$"
            decimalScale={2}
            fixedDecimalScale
            rules={{
              required: 'Price is required',
              min: { value: 1, message: 'Price must be at least $1.00' },
            }}
            InputProps={{
              endAdornment: <InputAdornment position="end">per customer</InputAdornment>,
            }}
          />
        )}
      />
      <div className={classes.spanAll}>
        <ModeAwareInput
          mode={mode}
          control={control}
          name="about.description"
          label="Description"
          renderEdit={(props) => (
            <TextArea
              {...generalInputProps}
              {...props}
              rules={{
                required: 'Description is required',
                validate: { validateWhitespace },
                maxLength: {
                  value: 185,
                  message: 'Description must be 185 characters or less',
                },
              }}
            />
          )}
        />
      </div>
    </FormFieldsGrid>
  );
};
